import mixpanel, { type Config, type Mixpanel } from "mixpanel-browser";

export type AnalyticsPage = {
  /** The page's product area; the app owns the values. */
  page_area?: string;
  project_key?: string;
  concept_key?: string;
  deliverable_key?: string;
};
/**
 * Events are sent only for a validated principal: without one (signed out, or the session is
 * unresolved) nothing is sent. User attributes are joined onto the principal key downstream, so
 * none are sent from here.
 */
export type AnalyticsContext = {
  /** Tracking consent; while false nothing initialises, identifies or sends. */
  trackingAllowed: boolean;
  principalKey?: string;
  environment?: string;
  /** The build the page runs. */
  appVersion?: string;
  /** Local deduplication only, never sent: a new pathname is a new page view. */
  pathname: string;
  page: AnalyticsPage;
};
type EventProperties = {
  page_viewed: AnalyticsPage;
  deliverable_downloaded: { project_key: string; deliverable_key: string };
  project_zip_downloaded: { project_key: string };
  /**
   * `link_url` is what was copied (the SDK caps every string at 255 characters); the current
   * page's properties are added.
   */
  project_link_shared: { project_key: string; link_url: string };
};
type EventName = keyof EventProperties;
type AnalyticsConfiguration = {
  token?: string;
  region?: string;
  /** Rewrites a pathname whose segments are secrets; every URL sent passes through it. */
  redactPath?: (pathname: string) => string;
};
type BeforeSend = NonNullable<Config["hooks"]["before_send_events"]>;

const DESTINATIONS: Record<string, string> = {
  US: "https://api-js.mixpanel.com",
  EU: "https://api-eu.mixpanel.com",
  IN: "https://api-in.mixpanel.com",
};
const EVENTS = new Set<EventName>([
  "page_viewed",
  "deliverable_downloaded",
  "project_zip_downloaded",
  "project_link_shared",
]);
/** Events about something done on a page, so they carry that page's properties too. */
const ON_PAGE = new Set<EventName>(["project_link_shared"]);
const PROPERTIES = new Set([
  "token",
  "distinct_id",
  "$user_id",
  "$device_id",
  "$insert_id",
  "time",
  "$ignore",
  "environment",
  "app_version",
  "principal_key",
  "page_area",
  "project_key",
  "concept_key",
  "deliverable_key",
  "link_url",
  // Device, page and referrer metadata the SDK adds to every event.
  "$os",
  "$browser",
  "$browser_version",
  "$device",
  "$screen_height",
  "$screen_width",
  "$current_url",
  "$referrer",
  "$referring_domain",
  "mp_lib",
  "$lib_version",
  // Added by track_pageview.
  "current_page_title",
  "current_domain",
  "current_url_path",
  "current_url_protocol",
  "current_url_search",
  // The current URL's campaign parameters; ad click ids are not among them.
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "utm_source_platform",
  "utm_campaign_id",
  "utm_creative_format",
  "utm_marketing_tactic",
]);

const redactUrl = (value: string, redactPath: (pathname: string) => string) => {
  try {
    const url = new URL(value);
    const pathname = redactPath(url.pathname);
    if (pathname === url.pathname) return value;
    url.pathname = pathname;
    return url.toString();
  } catch {
    return value;
  }
};

/**
 * Anything else the SDK adds (ad click ids, first-touch or persisted superproperties) never
 * leaves this boundary, no URL sent carries a secret path, and a property without a value is
 * left out rather than sent empty.
 */
const beforeSend =
  (redactPath: (pathname: string) => string): BeforeSend =>
  (event) => {
    if (!EVENTS.has(event.event as EventName)) return null;
    const clean = (key: string, value: unknown) => {
      if (typeof value !== "string") return value;
      if (key === "$current_url" || key === "$referrer") return redactUrl(value, redactPath);
      if (key === "current_url_path") return redactPath(value);
      return value;
    };
    return {
      ...event,
      properties: Object.fromEntries(
        Object.entries(event.properties)
          .filter(([key]) => PROPERTIES.has(key))
          .map(([key, value]) => [key, clean(key, value)])
          .filter(([, value]) => value != null && value !== ""),
      ),
    };
  };

/** Mixpanel drops events carrying `$ignore` at ingestion; automated browsers are test traffic. */
const isAutomation = () =>
  navigator.webdriver === true || navigator.userAgent.includes("HeadlessChrome");

/** A page without an area sends none, never a placeholder; while developing it is reported. */
const warnWithoutArea = (pathname: string) => {
  if (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "test") {
    console.warn(`No page_area for ${pathname}`);
  }
};

/** Without consent there is no visitor, so withdrawing consent resets the SDK like a sign-out. */
const visitorOf = (context?: AnalyticsContext) =>
  context?.trackingAllowed && context.principalKey ? context.principalKey : undefined;

export function createProductAnalytics() {
  let configuration: AnalyticsConfiguration = {};
  let sdk: Mixpanel | undefined;
  let context: AnalyticsContext | undefined;
  /** The principal the SDK is identified as. */
  let active: string | undefined;
  let failed = false;
  let lastPage: string | undefined;
  let automated = false;

  const safely = (action: () => void) => {
    try {
      action();
    } catch {
      /* Telemetry must never change product behavior. */
    }
  };

  // An unresolved session keeps the active visitor but sends nothing.
  const sending = () => !failed && active !== undefined && visitorOf(context) !== undefined;

  const send = <E extends EventName>(name: E, properties: EventProperties[E]) => {
    if (!sdk || !sending()) return;
    const sent = {
      ...(ON_PAGE.has(name) ? context?.page : {}),
      ...properties,
      environment: context?.environment,
      app_version: context?.appVersion,
      principal_key: active,
      ...(automated ? { $ignore: true } : {}),
    };
    // track_pageview adds the page's title, URL parts and campaign parameters.
    safely(() =>
      name === "page_viewed"
        ? sdk?.track_pageview(sent, { event_name: name })
        : sdk?.track(name, sent),
    );
  };

  const reset = () => {
    context = undefined;
    active = undefined;
    try {
      sdk?.reset();
    } catch {
      failed = true;
    }
  };

  const initialize = () => {
    if (sdk) return sdk;
    const token = configuration.token?.trim();
    const destination = configuration.region && DESTINATIONS[configuration.region];
    if (!token || !destination) return undefined;
    const config = {
      api_host: destination,
      autocapture: false,
      track_pageview: false,
      flags: false,
      remote_settings_mode: "disabled",
      record_sessions_percent: 0,
      record_heatmap_data: false,
      record_canvas: false,
      record_console: false,
      record_network: false,
      recording_event_triggers: {},
      track_marketing: true,
      save_referrer: false,
      store_google: false,
      skip_first_touch_marketing: true,
      stop_utm_persistence: true,
      // Mixpanel derives city, region and country from the request IP.
      ip: true,
      // No stored device identity, persisted properties/queues, or cross-tab coordination:
      // the device id lives only as long as the page.
      disable_persistence: true,
      batch_requests: false,
      hooks: {
        before_send_events: beforeSend(configuration.redactPath ?? ((pathname) => pathname)),
      },
      // 2.84 implements track_marketing but omits it from its bundled Config type.
    } satisfies Partial<Config> & { track_marketing: boolean };
    sdk = mixpanel.init(token, config, "cloud-product");
    automated = isAutomation();
    // No $identify or alias events; identify still sets distinct_id/$user_id.
    sdk.disable(["$identify", "$create_alias"]);
    return sdk;
  };

  const activate = (principalKey: string) => {
    if (failed) return;
    try {
      const instance = initialize();
      if (!instance) return;
      instance.identify(principalKey);
      if (instance.get_distinct_id() !== principalKey) {
        failed = true;
        return;
      }
      active = principalKey;
    } catch {
      failed = true;
    }
  };

  return {
    configure(next: AnalyticsConfiguration) {
      reset();
      lastPage = undefined;
      configuration = next;
      sdk = undefined;
      failed = false;
    },
    reset,
    update(next: AnalyticsContext) {
      const visitor = visitorOf(next);
      // Withdrawn consent resets like a sign-out. Otherwise there is no visitor while there is no
      // principal (signed out, or unresolved: every token renewal reads Me), and the active one is
      // kept but sends nothing.
      if (!visitor) {
        if (!next.trackingAllowed && active) reset();
        context = next;
        return;
      }
      if (active && active !== visitor) reset();
      context = next;
      if (!active) activate(visitor);
      if (!active) return;
      const pageIdentity = JSON.stringify([visitor, next.pathname]);
      if (lastPage !== pageIdentity) {
        lastPage = pageIdentity;
        if (!next.page.page_area) warnWithoutArea(next.pathname);
        send("page_viewed", next.page);
      }
    },
    /** Page views come from `update`, which deduplicates them. */
    track<E extends Exclude<EventName, "page_viewed">>(name: E, properties: EventProperties[E]) {
      send(name, properties);
    },
  };
}

export const productAnalytics = createProductAnalytics();
